import { useState } from "react";
import { FileText, X, Download, Trash2, Loader2, ChevronUp, Mail } from "lucide-react";
import { useOffer } from "@/context/OfferContext";
import api, { formatRON, formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function OfferTray() {
  const { items, remove, clear, count, total } = useOffer();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });

  if (count === 0) return null;

  const sendEmail = async () => {
    if (!form.contact_email.trim()) {
      toast.error(t("offer.emailRequired"));
      return;
    }
    setSending(true);
    try {
      await api.post("/offers/send",
        { items: items.map((i) => ({ product_id: i.id, quantity: 1 })), lang: (i18n.language || "ro").slice(0, 2), ...form });
      toast.success(t("offer.emailSent"));
      setDialog(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("offer.emailFailed"));
    } finally {
      setSending(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/offers/generate",
        { items: items.map((i) => ({ product_id: i.id, quantity: 1 })), lang: (i18n.language || "ro").slice(0, 2), ...form },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      a.href = url;
      a.download = (cd.match(/filename="?([^"]+)"?/) || [])[1] || "oferta-oxo.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("offer.success"));
      setDialog(false);
    } catch (e) {
      let detail;
      try {
        detail = e?.response?.data instanceof Blob
          ? JSON.parse(await e.response.data.text())?.detail
          : e?.response?.data?.detail;
      } catch {
        /* keep generic fallback */
      }
      toast.error(formatApiError(detail) || t("offer.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md" data-testid="offer-tray">
        <div className="bg-slate-900 text-white border border-slate-700 shadow-2xl">
          <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-slate-800 transition-colors" data-testid="offer-tray-toggle">
            <span className="flex items-center gap-2.5">
              <span className="relative flex items-center justify-center w-8 h-8 bg-orange-600">
                <FileText className="w-4 h-4" />
                <span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" data-testid="offer-count">{count}</span>
              </span>
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">{t("offer.trayTitle")}</span>
                <span className="block text-sm font-black">{formatRON(total)}</span>
              </span>
            </span>
            <ChevronUp className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "rotate-180"}`} />
          </button>

          {open && (
            <div className="border-t border-slate-800">
              <div className="max-h-52 overflow-y-auto divide-y divide-slate-800">
                {items.map((i) => (
                  <div key={i.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`offer-item-${i.id}`}>
                    <div className="flex-1 min-w-0">
                      {i.brand && <span className="text-[9px] uppercase tracking-wider font-bold text-orange-500">{i.brand}</span>}
                      <p className="text-xs text-slate-200 truncate">{i.name}</p>
                      <p className="text-xs font-bold text-white">{i.price ? formatRON(i.price) : t("offer.onRequest")}</p>
                    </div>
                    <button onClick={() => remove(i.id)} className="text-slate-500 hover:text-red-400 transition-colors" data-testid={`offer-remove-${i.id}`} aria-label="remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 p-3 border-t border-slate-800">
                <button onClick={clear} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-3 py-2.5 transition-colors" data-testid="offer-clear">
                  <Trash2 className="w-3.5 h-3.5" /> {t("offer.clear")}
                </button>
                <button onClick={() => setDialog(true)} className="flex-1 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid="offer-generate">
                  <FileText className="w-4 h-4" /> {t("offer.generate")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-md" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>{t("offer.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("offer.dialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="of-company">{t("offer.company")}</Label>
              <Input id="of-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="offer-company" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="of-name">{t("offer.name")}</Label>
                <Input id="of-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="offer-name" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="of-phone">{t("offer.phone")}</Label>
                <Input id="of-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="offer-phone" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="of-email">{t("offer.email")}</Label>
              <Input id="of-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="offer-email" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="of-notes">{t("offer.notes")}</Label>
              <Textarea id="of-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="offer-notes" />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <button onClick={sendEmail} disabled={sending || busy} className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-3 text-sm font-bold uppercase tracking-wide transition-colors w-full" data-testid="offer-send-email">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("offer.sending")}</> : <><Mail className="w-4 h-4" /> {t("offer.sendEmail")}</>}
            </button>
            <div className="flex items-center gap-3 w-full text-[10px] uppercase tracking-[0.2em] text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />{t("offer.orLabel")}<span className="h-px flex-1 bg-slate-200" />
            </div>
            <button onClick={generate} disabled={busy || sending} className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-emerald-700 disabled:opacity-60 text-white px-6 py-3 text-sm font-bold uppercase tracking-wide transition-colors w-full" data-testid="offer-download">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("offer.generating")}</> : <><Download className="w-4 h-4" /> {t("offer.download")}</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
